import { isRemoveEntityOperation, isSetEntityOperation, isUpdateEntityOperation, } from "@dataspecer/core/operation";
import { getSemanticModelMainEntity, isRdfsSnapshot } from "./concepts/index.js";
import { isCreateClassOperation, isChangeRdfsSourceUrlsOperation, isReloadOperation, isCreateGeneralizationOperation, isCreateRelationshipOperation, isDeleteEntityOperation, isModifyClassOperation, isModifyGeneralizationOperation, isModifyRelationEndOperation, isModifyRelationOperation, } from "./operations/operations.js";
import { normalizeLegacyAssignment } from "./profile/concepts/index.js";
import { createDefaultSemanticModelProfileOperationExecutor } from "./profile/operations/operations-executor.js";
/**
 * Applies semantic model operations to the given entities. The entities are
 * modified in place. Next to the semantic and profile operations, the generic
 * set/update/remove entity operations are supported as well.
 */
export function applyOperationsToSemanticModel(semanticModel, operations) {
    const updatedCollector = {};
    const removedCollector = [];
    const changes = [];
    const getEntity = (identifier) => semanticModel[identifier];
    const change = (updated, removed) => {
        for (const [id, written] of Object.entries(updated)) {
            // Operations recorded before the qualifiers became lowercase carry the legacy ones
            const entity = normalizeLegacyAssignment(written);
            changes.push({
                previous: semanticModel[id] ?? null,
                next: entity,
            });
            updatedCollector[id] = entity;
            semanticModel[id] = entity;
        }
        for (const id of removed) {
            changes.push({
                previous: semanticModel[id] ?? null,
                next: null,
            });
            removedCollector.push(id);
            delete semanticModel[id];
        }
    };
    const profileExecutor = createDefaultSemanticModelProfileOperationExecutor({ entity: (identifier) => getEntity(identifier) ?? null }, { change });
    const result = [];
    for (const operation of operations) {
        if (isReloadOperation(operation)) {
            result.push({ success: true });
        }
        else if (isChangeRdfsSourceUrlsOperation(operation)) {
            const entity = getSemanticModelMainEntity(semanticModel);
            if (entity && isRdfsSnapshot(entity.source)) {
                const updated = { ...entity, source: { ...entity.source, urls: [...operation.urls] } };
                change({ [entity.id]: updated }, []);
                result.push({ success: true });
            }
            else {
                result.push({ success: false });
            }
        }
        else if (isSetEntityOperation(operation)) {
            result.push(handleSetEntityOperation(change, operation));
        }
        else if (isUpdateEntityOperation(operation)) {
            result.push(handleUpdateEntityOperation(getEntity, change, operation));
        }
        else if (isRemoveEntityOperation(operation)) {
            result.push(handleRemoveEntityOperation(getEntity, change, operation));
        }
        else if (isCreateClassOperation(operation)) {
            result.push(handleCreateClassOperation(getEntity, change, operation));
        }
        else if (isModifyClassOperation(operation)) {
            result.push(handleModifyClassOperation(getEntity, change, operation));
        }
        else if (isCreateRelationshipOperation(operation)) {
            result.push(handleCreateRelationshipOperation(getEntity, change, operation));
        }
        else if (isModifyRelationOperation(operation)) {
            result.push(handleModifyRelationOperation(getEntity, change, operation));
        }
        else if (isModifyRelationEndOperation(operation)) {
            result.push(handleModifyRelationEndOperation(getEntity, change, operation));
        }
        else if (isCreateGeneralizationOperation(operation)) {
            result.push(handleCreateGeneralizationOperation(getEntity, change, operation));
        }
        else if (isModifyGeneralizationOperation(operation)) {
            result.push(handleModifyGeneralizationOperation(getEntity, change, operation));
        }
        else if (isDeleteEntityOperation(operation)) {
            result.push(handleDeleteEntityOperation(getEntity, change, operation));
        }
        else {
            const operationResult = profileExecutor.executeOperation(operation);
            if (operationResult !== null) {
                result.push({
                    success: true,
                    id: operationResult.created[0],
                });
            }
            else {
                // Unknown operation.
                result.push({
                    success: false,
                });
            }
        }
    }
    return {
        result,
        changes,
        updated: updatedCollector,
        removed: removedCollector,
    };
}
function handleSetEntityOperation(change, operation) {
    change({ [operation.entity.id]: operation.entity }, []);
    return {
        success: true,
    };
}
function handleUpdateEntityOperation(getEntity, change, operation) {
    const entity = getEntity(operation.entityId);
    if (!entity) {
        return {
            success: false,
        };
    }
    change({ [operation.entityId]: { ...entity, ...operation.update } }, []);
    return {
        success: true,
    };
}
function handleRemoveEntityOperation(getEntity, change, operation) {
    if (!getEntity(operation.entityId)) {
        return {
            success: false,
        };
    }
    change({}, [operation.entityId]);
    return {
        success: true,
    };
}
function handleModifyGeneralizationOperation(getEntity, change, operation) {
    if (!getEntity(operation.entityId)) {
        return {
            success: false,
        };
    }
    change({ [operation.entityId]: { ...getEntity(operation.entityId), ...operation.entity } }, []);
    return {
        success: true,
    };
}
function handleDeleteEntityOperation(getEntity, change, operation) {
    if (!getEntity(operation.entityId)) {
        return {
            success: false,
        };
    }
    change({}, [operation.entityId]);
    return {
        success: true,
    };
}
function handleCreateClassOperation(getEntity, change, operation) {
    const id = operation.entity.id;
    if (getEntity(id)) {
        return {
            success: false,
        };
    }
    const newClass = {
        // Store any other data
        ...operation.entity,
        id,
        iri: operation.entity.iri ?? null,
        type: ["class"],
        name: operation.entity.name ?? {},
        description: operation.entity.description ?? {},
        externalDocumentationUrl: operation.entity.externalDocumentationUrl ?? null,
    };
    change({ [id]: newClass }, []);
    return {
        success: true,
        id,
    };
}
function handleModifyClassOperation(getEntity, change, operation) {
    if (!getEntity(operation.entityId)) {
        return {
            success: false,
        };
    }
    change({ [operation.entityId]: { ...getEntity(operation.entityId), ...operation.entity } }, []);
    return {
        success: true,
    };
}
function handleCreateRelationshipOperation(getEntity, change, operation) {
    const id = operation.entity.id;
    if (getEntity(id)) {
        return {
            success: false,
        };
    }
    const relationship = {
        ...operation.entity,
        id,
        type: ["relationship"],
        iri: operation.entity.iri ?? null,
        name: operation.entity.name ?? {},
        description: operation.entity.description ?? {},
        ends: [
            {
                ...operation.entity.ends?.[0],
                name: operation.entity.ends?.[0]?.name ?? {},
                description: operation.entity.ends?.[0]?.description ?? {},
                cardinality: operation.entity.ends?.[0]?.cardinality,
                concept: operation.entity.ends?.[0]?.concept ?? null,
                iri: operation.entity.ends?.[0]?.iri ?? null,
                externalDocumentationUrl: operation.entity.ends?.[0]?.externalDocumentationUrl ?? null,
            },
            {
                ...operation.entity.ends?.[1],
                name: operation.entity.ends?.[1]?.name ?? {},
                description: operation.entity.ends?.[1]?.description ?? {},
                cardinality: operation.entity.ends?.[1]?.cardinality,
                concept: operation.entity.ends?.[1]?.concept ?? null,
                iri: operation.entity.ends?.[1]?.iri ?? null,
                externalDocumentationUrl: operation.entity.ends?.[1]?.externalDocumentationUrl ?? null,
            },
        ],
    };
    change({ [id]: relationship }, []);
    return {
        success: true,
        id,
    };
}
function handleModifyRelationOperation(getEntity, change, operation) {
    const oldRelationship = getEntity(operation.entityId);
    if (!oldRelationship) {
        return {
            success: false,
        };
    }
    const updatedRelationship = {
        ...oldRelationship,
        ...operation.entity,
        ends: [
            {
                ...oldRelationship.ends[0],
                ...operation.entity.ends?.[0],
            },
            {
                ...oldRelationship.ends[1],
                ...operation.entity.ends?.[1],
            },
        ],
        name: operation.entity.name ?? oldRelationship.name,
        description: operation.entity.description ?? oldRelationship.description,
        iri: operation.entity.iri ?? oldRelationship.iri,
    };
    change({ [operation.entityId]: updatedRelationship }, []);
    return {
        success: true,
    };
}
function handleModifyRelationEndOperation(getEntity, change, operation) {
    const oldRelationship = getEntity(operation.entityId);
    if (!oldRelationship) {
        return {
            success: false,
        };
    }
    const previousEnd = oldRelationship.ends[operation.endIndex];
    if (previousEnd === undefined) {
        return {
            success: false,
        };
    }
    const ends = [...oldRelationship.ends];
    ends[operation.endIndex] = { ...previousEnd, ...operation.end };
    change({ [operation.entityId]: { ...oldRelationship, ends } }, []);
    return {
        success: true,
    };
}
function handleCreateGeneralizationOperation(getEntity, change, operation) {
    const id = operation.entity.id;
    if (getEntity(id)) {
        return {
            success: false,
        };
    }
    const generalization = {
        id,
        iri: operation.entity.iri ?? null,
        child: operation.entity.child ?? "",
        parent: operation.entity.parent ?? "",
        type: ["generalization"],
    };
    change({ [id]: generalization }, []);
    return {
        success: true,
        id,
    };
}
//# sourceMappingURL=apply-operations.js.map